import type { PolicyComponents } from "@policystack/react/policy";
import { Children, type ReactNode } from "react";

import { PageDescription } from "#/components/PageDescription";
import { PageEyeBrow } from "#/components/PageEyeBrow";
import { PageHeader } from "#/components/PageHeader";
import { PageIntro } from "#/components/PageIntro";
import { PageShell } from "#/components/PageShell";
import {
  Table as DataTable,
  TableBody,
  TableCell as DataTableCell,
  TableHead,
  TableHeader,
  TableRow as DataTableRow,
} from "#/components/ui/table";
import { cn } from "#/lib/utils";

import { SectionHeader } from "./SectionHeader";

export const policyComponents: PolicyComponents = {
  Root: ({ children }) => (
    <div className="max-w-3xl space-y-10 text-[15px] leading-7 text-foreground">{children}</div>
  ),
  Section: ({ children }) => (
    <section className="flex flex-col gap-4 border-b border-border pb-10 last:border-b-0 last:pb-0">
      {children}
    </section>
  ),
  Heading: ({ node }) => {
    const level = node.level ?? 2;
    if (level <= 2) {
      return <SectionHeader>{node.value}</SectionHeader>;
    }
    return <h3 className="text-lg font-semibold text-foreground">{node.value}</h3>;
  },
  Paragraph: ({ children }) => (
    <p className="text-[15px] leading-7 text-muted-foreground">{children}</p>
  ),
  Bold: ({ node }) => (
    <strong className="font-semibold text-foreground">
      {node.value.endsWith(" ") ? node.value : `${node.value}\u00a0`}
    </strong>
  ),
  List: ({ node, children }) => {
    const Tag = node.ordered ? "ol" : "ul";
    return (
      <Tag
        className={cn(
          "space-y-2 pl-5 text-[15px] leading-7 text-muted-foreground",
          node.ordered ? "list-decimal" : "list-disc",
        )}
      >
        {children}
      </Tag>
    );
  },
  ListItem: ({ children }) => <li className="pl-1">{children}</li>,
  Table: ({ children }) => {
    const childArray = Children.toArray(children);
    const [headerRow, ...bodyRows] = childArray;
    return (
      <div className="-mx-4 px-4 sm:mx-0 sm:px-0">
        <DataTable className="min-w-xl">
          <TableHeader className="bg-muted/30">{headerRow}</TableHeader>
          {bodyRows.length > 0 ? <TableBody>{bodyRows}</TableBody> : null}
        </DataTable>
      </div>
    );
  },
  TableHeaderRow: ({ children }) => <DataTableRow>{children}</DataTableRow>,
  TableHeaderCell: ({ children }) => (
    <TableHead
      scope="col"
      className="h-auto px-4 py-3 text-xs font-semibold uppercase tracking-wide whitespace-normal text-muted-foreground"
    >
      {children}
    </TableHead>
  ),
  TableRow: ({ children }) => <DataTableRow>{children}</DataTableRow>,
  TableCell: ({ children }) => (
    <DataTableCell className="px-4 py-3 align-top text-[15px] leading-6 whitespace-normal text-muted-foreground">
      {children}
    </DataTableCell>
  ),
  Link: ({ node }) => (
    <a
      href={node.href}
      className="text-primary underline underline-offset-4 hover:text-foreground"
      rel={node.href.startsWith("http") ? "noreferrer noopener" : undefined}
      target={node.href.startsWith("http") ? "_blank" : undefined}
    >
      {node.value}
    </a>
  ),
};

export function PolicyPageLayout({
  eyebrow,
  title,
  description,
  children,
}: {
  eyebrow: string;
  title: string;
  description: string;
  children: ReactNode;
}) {
  return (
    <PageShell>
      <PageIntro align="start">
        <PageEyeBrow>{eyebrow}</PageEyeBrow>
        <PageHeader>{title}</PageHeader>
        <PageDescription>{description}</PageDescription>
      </PageIntro>
      <div className="mt-10">{children}</div>
    </PageShell>
  );
}
